/**
 * CtaAdvancedOptions — collapsible "opções avançadas" block used inside
 * each CTA card in MainSettings (primary + secondary).
 *
 * Renders — subtitle PT/EN/ES, colours (text + bg), font, shape, effect,
 * per-button size + offsets, action (internal/external), target page and
 * visibility. Existing basic fields (label, url, color, icon) stay untouched
 * above this block to preserve prior admin behaviour.
 */
import React, { useState } from "react";
import { ChevronDown, ChevronRight, RotateCcw, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SelectField, RangeField, ColorField } from "./fields.jsx";
import { LANGS, MultiLangText } from "./shared.jsx";

const FONT_OPTIONS = [
  { v: "Fredoka", l: "Fredoka (padrão)" },
  { v: "Outfit", l: "Outfit" },
  { v: "Poppins", l: "Poppins" },
  { v: "Inter", l: "Inter" },
  { v: "Bebas Neue", l: "Bebas Neue" },
  { v: "Montserrat", l: "Montserrat" },
];
const SHAPE_OPTIONS = [
  { v: "rounded", l: "Arredondado" },
  { v: "square", l: "Quadrado" },
  { v: "pill", l: "Pílula (totalmente redondo)" },
];
const EFFECT_OPTIONS = [
  { v: "none", l: "Nenhum" },
  { v: "glow", l: "Glow (brilho pulsante)" },
  { v: "pulse", l: "Pulse (escala)" },
  { v: "shimmer", l: "Shimmer (reflexo)" },
  { v: "bounce", l: "Bounce (saltinho)" },
  { v: "wobble", l: "Wobble (balança)" },
  { v: "opacity", l: "Opacidade (fade pulsante)" },
];
const ICON_OPTIONS = [
  { v: "", l: "— nenhum —" },
  { v: "School", l: "Escola" },
  { v: "FileText", l: "Documento/Regulamento" },
  { v: "Users", l: "Turma/Pessoas" },
  { v: "BarChart3", l: "Estatísticas/Ranking" },
  { v: "Award", l: "Diploma" },
  { v: "Trophy", l: "Troféu" },
  { v: "Crown", l: "Coroa" },
  { v: "Medal", l: "Medalha" },
  { v: "Star", l: "Estrela" },
  { v: "Sparkles", l: "Brilhos" },
  { v: "Gamepad2", l: "Comando/Jogar" },
  { v: "Clock", l: "Relógio" },
  { v: "Timer", l: "Cronómetro" },
  { v: "Bot", l: "Robô" },
  { v: "BookOpen", l: "Livro" },
  { v: "Mail", l: "Email" },
  { v: "MessageCircle", l: "WhatsApp/Chat" },
  { v: "Home", l: "Casa" },
  { v: "LogOut", l: "Sair" },
  { v: "Globe", l: "Globo/Idiomas" },
  { v: "Zap", l: "Raio" },
  { v: "Flame", l: "Chama" },
  { v: "Heart", l: "Coração" },
  { v: "ThumbsUp", l: "Gosto" },
  { v: "Rocket", l: "Foguete" },
  { v: "Target", l: "Alvo" },
  { v: "Gem", l: "Gema" },
  { v: "Diamond", l: "Diamante" },
];
const PAGE_OPTIONS = [
  { v: "", l: "— manter URL atual —" },
  { v: "/", l: "Início (/)" },
  { v: "/inscrever-escola", l: "Inscrever Escola (/inscrever-escola)" },
  { v: "/regulamento", l: "Regulamento (/regulamento)" },
  { v: "/treinar", l: "Treinar (/treinar)" },
  { v: "/competir", l: "Competir (/competir)" },
  { v: "/resultados", l: "Resultados (/resultados)" },
  { v: "/resultados-competicao", l: "Resultados Grande Final / Competição (/resultados-competicao)" },
  { v: "/diploma-individual", l: "Diploma Individual (/diploma-individual)" },
  { v: "/diploma-massa", l: "Diploma em Massa (/diploma-massa)" },
  { v: "/eliminatorias", l: "Eliminatórias (/eliminatorias)" },
  { v: "/vs-robot", l: "VS Robô (/vs-robot)" },
  { v: "/vs-humano", l: "VS Humano (/vs-humano)" },
  { v: "/aluno-login", l: "Login Aluno (/aluno-login)" },
];

const ACTION_OPTIONS = [
  { v: "internal", l: "Página interna" },
  { v: "external", l: "URL externa (abre em nova aba)" },
];

const DEFAULTS = {
  subtitle_pt: "", subtitle_en: "", subtitle_es: "",
  text_color: "#0A0E2A",
  bg_color: "#E5FF00",
  font: "Fredoka",
  shape: "rounded",
  effect: "none",
  size: 0,        // 0 = usar altura padrão da linha
  font_size: 0,   // 0 = usar tamanho padrão
  offset_x: 0,
  offset_y: 0,
  action_type: "internal",
  target_page: "",
  visible: true,
};

export function CtaAdvancedOptions({ cta, onChange, testidPrefix }) {
  const [open, setOpen] = useState(false);
  const c = cta || {};
  const set = (key, value) => onChange({ ...c, [key]: value });
  const visible = c.visible !== false; // default true

  const reset = () => {
    // Repor apenas os campos avançados; manter os campos base (label/url/icon/color).
    onChange({ ...c, ...DEFAULTS });
  };

  return (
    <div className="rounded-md border border-teal-100 bg-teal-50/40" data-testid={`${testidPrefix}-advanced`}>
      <div className="flex items-center justify-between px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-teal-800 hover:text-teal-900"
          data-testid={`${testidPrefix}-advanced-toggle`}
        >
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          Opções avançadas
        </button>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={reset}
            className="flex items-center gap-1 text-[11px] text-rose-600 hover:text-rose-700 font-semibold"
            data-testid={`${testidPrefix}-advanced-reset`}
          >
            <RotateCcw className="w-3 h-3" /> Repor
          </button>
          <label className="flex items-center gap-1 text-[11px] font-semibold text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={visible}
              onChange={(e) => set("visible", e.target.checked)}
              className="w-3.5 h-3.5 accent-teal-600"
              data-testid={`${testidPrefix}-visible`}
            />
            {visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} Mostrar
          </label>
        </div>
      </div>

      {open && (
        <div className="border-t border-teal-100 p-3 space-y-3">
          {/* Subtítulo PT/EN/ES */}
          <MultiLangText
            label="Subtítulo (opcional — aparece abaixo do texto principal)"
            testidPrefix={`${testidPrefix}-subtitle`}
            value={{ pt: c.subtitle_pt || "", en: c.subtitle_en || "", es: c.subtitle_es || "" }}
            onChange={(code, v) => set(`subtitle_${code}`, v)}
          />

          {/* Cores */}
          <div className="grid sm:grid-cols-2 gap-3">
            <ColorField
              label="Cor do texto"
              value={c.text_color || c.fg || "#0A0E2A"}
              onChange={(v) => set("text_color", v)}
              testid={`${testidPrefix}-text-color`}
            />
            <ColorField
              label="Cor de fundo"
              value={c.bg_color || c.bg || "#E5FF00"}
              onChange={(v) => set("bg_color", v)}
              testid={`${testidPrefix}-bg-color`}
            />
          </div>

          {/* Fonte + Forma */}
          <div className="grid sm:grid-cols-2 gap-3">
            <SelectField
              label="Fonte"
              value={c.font || "Fredoka"}
              onChange={(v) => set("font", v)}
              options={FONT_OPTIONS}
              testid={`${testidPrefix}-font`}
            />
            <SelectField
              label="Forma"
              value={c.shape || "rounded"}
              onChange={(v) => set("shape", v)}
              options={SHAPE_OPTIONS}
              testid={`${testidPrefix}-shape`}
            />
          </div>

          {/* Ícone + Efeito */}
          <div className="grid sm:grid-cols-2 gap-3">
            <SelectField
              label="Ícone"
              value={c.icon || ""}
              onChange={(v) => set("icon", v)}
              options={ICON_OPTIONS}
              testid={`${testidPrefix}-icon-select`}
            />
            <SelectField
              label="Efeito"
              value={c.effect || "none"}
              onChange={(v) => set("effect", v)}
              options={EFFECT_OPTIONS}
              testid={`${testidPrefix}-effect`}
            />
          </div>

          {/* Tamanho + Posição X/Y */}
          <div className="grid sm:grid-cols-3 gap-3">
            <RangeField
              label="Tamanho (altura, 0 = padrão)"
              value={c.size || 0}
              min={0}
              max={100}
              unit="px"
              onChange={(v) => set("size", v)}
              testid={`${testidPrefix}-size`}
            />
            <RangeField
              label="Posição X"
              value={c.offset_x || 0}
              min={-200}
              max={200}
              unit="px"
              onChange={(v) => set("offset_x", v)}
              testid={`${testidPrefix}-offset-x`}
            />
            <RangeField
              label="Posição Y"
              value={c.offset_y || 0}
              min={-200}
              max={200}
              unit="px"
              onChange={(v) => set("offset_y", v)}
              testid={`${testidPrefix}-offset-y`}
            />
          </div>

          {/* Tamanho do texto */}
          <RangeField
            label="Tamanho do texto (0 = padrão da linha)"
            value={c.font_size || 0}
            min={0}
            max={36}
            unit="px"
            onChange={(v) => set("font_size", v)}
            testid={`${testidPrefix}-font-size`}
          />

          {/* Ação ao clicar + página de destino */}
          <div className="grid sm:grid-cols-2 gap-3">
            <SelectField
              label="Ação ao clicar"
              value={c.action_type || "internal"}
              onChange={(v) => set("action_type", v)}
              options={ACTION_OPTIONS}
              testid={`${testidPrefix}-action-type`}
            />
            {(c.action_type || "internal") === "internal" ? (
              <SelectField
                label="Página de destino"
                value={c.target_page || ""}
                onChange={(v) => {
                  // Se selecionar uma página, também escrever no `url` para
                  // manter compatibilidade com o resto do sistema.
                  set("target_page", v);
                  if (v) onChange({ ...c, target_page: v, url: v });
                }}
                options={PAGE_OPTIONS}
                testid={`${testidPrefix}-target-page`}
              />
            ) : (
              <div>
                <Label className="text-xs text-slate-600">URL externa por idioma (https://…)</Label>
                <p className="text-[10px] text-slate-400 mb-1">O botão abre o link do idioma ativo. Se um idioma ficar vazio, usa o campo PT.</p>
                <div className="space-y-1.5">
                  {[
                    { code: "pt", label: "PT" },
                    { code: "en", label: "EN" },
                    { code: "es", label: "ES" },
                  ].map(({ code, label }) => (
                    <div key={code} className="flex items-center gap-2">
                      <span className="w-7 text-[11px] font-bold text-slate-500">{label}</span>
                      <Input
                        value={code === "pt" ? (c.url_pt ?? c.url ?? "") : (c[`url_${code}`] || "")}
                        onChange={(e) => {
                          // Keep PT mirrored into the legacy `url` field so older
                          // readers (and the PT fallback) stay in sync.
                          if (code === "pt") onChange({ ...c, url_pt: e.target.value, url: e.target.value });
                          else set(`url_${code}`, e.target.value);
                        }}
                        placeholder={code === "pt" ? "https://exemplo.pt" : `https://exemplo.${code}`}
                        className="flex-1"
                        data-testid={`${testidPrefix}-external-url-${code}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
